import React, { useState, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import { MapPin, Navigation, AlertCircle, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card } from '../ui/card';
import { toast } from 'sonner';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

// Fix Leaflet default marker icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

export const LocationCapture = ({ onNext }) => {
  const { t } = useTranslation();
  const [location, setLocation] = useState(null);
  const [address, setAddress] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [manualAddress, setManualAddress] = useState(false);

  useEffect(() => {
    getCurrentLocation();
  }, []);

  const getCurrentLocation = () => {
    setIsLoading(true);
    setError('');

    if (!navigator.geolocation) {
      setError('Geolocation is not supported by your browser');
      setIsLoading(false);
      setManualAddress(true);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        setLocation({ latitude, longitude });

        // Reverse geocode to get address
        try {
          const response = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}`
          );
          const data = await response.json();
          setAddress(data.display_name || '');
        } catch (err) {
          console.error('Geocoding error:', err);
          setAddress('Address could not be determined');
        }

        setIsLoading(false);
      },
      (err) => {
        console.error('Geolocation error:', err);
        setError(t('roadside.errors.gpsBlockedMsg'));
        setIsLoading(false);
        setManualAddress(true);
        toast.error(t('roadside.errors.gpsBlocked'), {
          description: t('roadside.errors.gpsBlockedMsg')
        });
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0
      }
    );
  };

  const handleManualAddress = async () => {
    if (!address.trim()) {
      toast.error(t('roadside.errors.addressRequired'));
      return;
    }

    setIsLoading(true);
    try {
      // Geocode the address
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(address)}`
      );
      const data = await response.json();

      if (data && data.length > 0) {
        setLocation({
          latitude: parseFloat(data[0].lat),
          longitude: parseFloat(data[0].lon)
        });
        setAddress(data[0].display_name);
        setManualAddress(false);
        toast.success(t('roadside.success.locationFound'));
      } else {
        toast.error(t('roadside.errors.addressNotFound'), {
          description: t('roadside.errors.addressNotFoundMsg')
        });
      }
    } catch (err) {
      toast.error(t('roadside.errors.addressNotFound'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleNext = () => {
    if (!location || !address) {
      toast.error(t('roadside.errors.addressRequired'), {
        description: 'Please confirm your location before proceeding'
      });
      return;
    }

    onNext({
      latitude: location.latitude,
      longitude: location.longitude,
      address: address
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 to-gray-50 pb-safe">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-4 py-4 sticky top-0 z-50">
        <div className="max-w-2xl mx-auto">
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <MapPin className="w-6 h-6 text-red-600" />
            {t('roadside.title')}
          </h1>
          <p className="text-sm text-gray-600 mt-1">{t('roadside.step1.step')}: {t('roadside.step1.title')}</p>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-6 space-y-6">
        {/* Map Card */}
        {location && !manualAddress && (
          <Card className="p-0 overflow-hidden">
            <div className="h-64 sm:h-80">
              <MapContainer
                center={[location.latitude, location.longitude]}
                zoom={15}
                style={{ height: '100%', width: '100%' }}
                zoomControl={true}
              >
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                <Marker position={[location.latitude, location.longitude]}>
                  <Popup>Your location</Popup>
                </Marker>
              </MapContainer>
            </div>
          </Card>
        )}

        {/* Loading State */}
        {isLoading && !location && (
          <Card className="p-8 text-center">
            <Loader2 className="w-12 h-12 text-red-600 mx-auto mb-4 animate-spin" />
            <p className="text-gray-600">{t('roadside.step1.gettingLocation')}</p>
          </Card>
        )}

        {/* Error State */}
        {error && (
          <Card className="p-6 bg-yellow-50 border-yellow-200">
            <div className="flex gap-3">
              <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-yellow-900">{t('roadside.step1.locationError')}</p>
                <p className="text-sm text-yellow-800 mt-1">{error}</p>
              </div>
            </div>
          </Card>
        )}

        {/* Address Card */}
        <Card className="p-6">
          <Label className="text-lg font-semibold text-gray-900 mb-3 block">
            {t('roadside.step1.title')}
          </Label>

          {!manualAddress && location ? (
            <div className="space-y-4">
              <div className="bg-green-50 border-2 border-green-200 rounded-lg p-4">
                <p className="text-sm text-green-800 font-medium mb-1">{t('roadside.step1.currentLocation')}</p>
                <p className="text-gray-900">{address}</p>
              </div>

              <Button
                variant="outline"
                onClick={() => setManualAddress(true)}
                className="w-full"
              >
                {t('roadside.step1.editAddress')}
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              <Input
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                placeholder={t('roadside.step1.enterAddress')}
                className="text-base"
              />
              <div className="flex gap-2">
                <Button
                  onClick={handleManualAddress}
                  disabled={isLoading || !address.trim()}
                  className="flex-1 bg-gray-900 hover:bg-gray-800"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 rtl:ml-2 rtl:mr-0 animate-spin" />
                      {t('roadside.step1.finding')}
                    </>
                  ) : (
                    t('roadside.step1.confirmAddress')
                  )}
                </Button>
                {location && (
                  <Button
                    variant="outline"
                    onClick={() => setManualAddress(false)}
                  >
                    {t('common.cancel')}
                  </Button>
                )}
              </div>
            </div>
          )}
        </Card>

        {/* Retry GPS */}
        {error && (
          <Button
            onClick={getCurrentLocation}
            variant="outline"
            className="w-full border-2"
          >
            <Navigation className="w-4 h-4 mr-2 rtl:ml-2 rtl:mr-0" />
            {t('roadside.step1.tryAgain')}
          </Button>
        )}

        {/* Next Button */}
        <Button
          onClick={handleNext}
          disabled={!location || !address}
          size="lg"
          className="w-full bg-red-600 hover:bg-red-700 text-white py-6 text-lg font-semibold shadow-lg"
        >
          {t('roadside.helpNeeded')}
        </Button>

        {/* Info */}
        <div className="text-center text-sm text-gray-600 px-4">
          <p>{t('roadside.step1.dispatch')}</p>
        </div>
      </div>
    </div>
  );
};
